import axiosInstance from '../utils/axios.instance';
import { API_ENDPOINTS } from '../config/api.config';

/**
 * Service d'authentification
 */
export const authService = {
  /**
   * Inscription d'un nouvel utilisateur
   * @param {Object} userData - Données de l'utilisateur
   * @returns {Promise}
   */
  register: async (userData) => {
    try {
      const response = await axiosInstance.post(API_ENDPOINTS.AUTH.REGISTER, userData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Connexion
   * @param {string} email
   * @param {string} password
   * @returns {Promise}
   */
  login: async (email, password) => {
    try {
      const response = await axiosInstance.post(API_ENDPOINTS.AUTH.LOGIN, {
        email,
        password,
      });

      if (response.success && response.data) {
        const { accessToken, refreshToken, user } = response.data;

        // Sauvegarder les tokens et les infos utilisateur
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem('user', JSON.stringify(user));

        return response.data;
      }

      throw new Error('Réponse de connexion invalide');
    } catch (error) {
      throw error;
    }
  },

  /**
   * Rafraîchir le token
   * @param {string} refreshToken
   * @returns {Promise}
   */
  refreshToken: async (refreshToken) => {
    try {
      const response = await axiosInstance.post(API_ENDPOINTS.AUTH.REFRESH, {
        refreshToken,
      });

      if (response.success && response.data) {
        const { accessToken, refreshToken: newRefreshToken } = response.data;

        localStorage.setItem('accessToken', accessToken);
        if (newRefreshToken) {
          localStorage.setItem('refreshToken', newRefreshToken);
        }

        return response.data;
      }

      throw new Error('Échec du rafraîchissement du token');
    } catch (error) {
      throw error;
    }
  },

  /**
   * Déconnexion
   * @returns {Promise}
   */
  logout: async () => {
    try {
      const token = localStorage.getItem('accessToken');

      if (token) {
        await axiosInstance.post(API_ENDPOINTS.AUTH.LOGOUT, { token });
      }
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    } finally {
      // Nettoyer le localStorage même si la requête échoue
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
    }
  },

  /**
   * Changer le mot de passe
   * @param {string} currentPassword
   * @param {string} newPassword
   * @returns {Promise}
   */
  changePassword: async (currentPassword, newPassword) => {
    try {
      const response = await axiosInstance.put(API_ENDPOINTS.AUTH.CHANGE_PASSWORD, {
        currentPassword,
        newPassword,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Récupérer l'utilisateur actuel depuis le localStorage
   * @returns {Object|null}
   */
  getCurrentUser: () => {
    try {
      const user = localStorage.getItem('user');
      return user ? JSON.parse(user) : null;
    } catch (error) {
      return null;
    }
  },

  /**
   * Vérifier si l'utilisateur est connecté
   * @returns {boolean}
   */
  isAuthenticated: () => {
    const token = localStorage.getItem('accessToken');
    return !!token;
  },

  /**
   * Vérifier si l'utilisateur a un rôle spécifique
   * @param {string} role
   * @returns {boolean}
   */
  hasRole: (role) => {
    const user = authService.getCurrentUser();
    return user?.roles?.includes(role) || false;
  },

  /**
   * Vérifier si l'utilisateur est admin
   * @returns {boolean}
   */
  isAdmin: () => {
    return authService.hasRole('ROLE_ADMIN');
  },
};

export default authService;
